// Form-workflow table columns — the domain cells (workflow identity + version
// chip, status chip with an optional "old versions" note, relative last-update)
// plus the trailing row-actions kebab, rendered inside the common DataTable in
// FormWorkflowModal. The kebab was removed in the 2026-09-14 verb-gate audit for
// being wholly inert; it is back because all three of its items now have
// endpoints — duplicate, versions and the status lifecycle. See
// WorkflowRowActions for the per-item gating.

import { useTranslation } from 'react-i18next';
import { Chip, Icon, type DataTableColumn } from '@/components/common';
import { WorkflowListingState, type WorkflowItem } from '@/api/endpoints/workflowsApi';
import WorkflowRowActions from './WorkflowRowActions';
import { fmtRelative } from '@/utils/formatDate';

// Published shows the success chip; draft AND both show the neutral "Draft"
// chip (both additionally shows the amber "old versions" note in the cell).
const StatusChip = ({ state, label }: { state: WorkflowListingState; label: string }) =>
  state === WorkflowListingState.Published ? (
    <Chip variant='success' iconStart='circle-check'>
      {label}
    </Chip>
  ) : (
    <Chip variant='neutral' iconStart='pencil'>
      {label}
    </Chip>
  );

interface UseWorkflowColumnsArgs {
  /** Row kebab's Version history. Required, and deliberately not verb-gated:
   *  workflow `view` is already enforced by the modal's own entry point, and
   *  keeping it unconditional is what stops the kebab rendering empty. */
  onVersions: (w: WorkflowItem) => void;
  /** Supplied by the modal only when the matching verb is held
   *  (Duplicate→create, Archive/Restore→edit, Delete→delete) AND the tab
   *  applies — Duplicate and Archive are Workflows-tab only, Restore and
   *  Delete are Archived-tab only. Absent = hidden. */
  onDuplicate?: (w: WorkflowItem) => void;
  onArchive?: (w: WorkflowItem) => void;
  onRestore?: (w: WorkflowItem) => void;
  onDelete?: (w: WorkflowItem) => void;
}

export const useWorkflowColumns = ({
  onVersions,
  onDuplicate,
  onArchive,
  onRestore,
  onDelete,
}: UseWorkflowColumnsArgs): DataTableColumn<WorkflowItem>[] => {
  const { t } = useTranslation();

  return [
    {
      key: 'name',
      label: t('forms_workflow_col_workflow'),
      width: 'minmax(18rem, 2fr)',
      render: (w) => (
        <span className='forms-table__name'>
          <span className='forms-table__icon' aria-hidden='true'>
            <Icon name='workflow' size={16} />
          </span>
          <span className='forms-table__name-text'>{w.name}</span>
          {w.version && <Chip variant='neutral'>{w.version}</Chip>}
        </span>
      ),
    },
    {
      key: 'status',
      label: t('forms_workflow_col_status'),
      width: 'minmax(14rem, 1.4fr)',
      render: (w) => (
        <span className='form-workflow__status-cell'>
          <StatusChip
            state={w.state}
            label={
              w.state === WorkflowListingState.Published
                ? t('forms_workflow_status_published')
                : t('forms_workflow_status_draft')
            }
          />
          {w.state === WorkflowListingState.Both && (
            <Chip variant='warning'>{t('forms_workflow_old_versions')}</Chip>
          )}
        </span>
      ),
    },
    {
      key: 'updated',
      label: t('forms_workflow_col_updated'),
      render: (w) => <span className='forms-table__muted'>{fmtRelative(w.updatedAt, t)}</span>,
    },
    {
      key: 'actions',
      label: '',
      align: 'end',
      // Narrower than the templates table's 4.5rem — this trigger is the
      // size='sm' IconButton the in-modal tables use.
      width: '3.5rem',
      render: (w) => (
        <WorkflowRowActions
          onVersions={() => onVersions(w)}
          onDuplicate={onDuplicate && (() => onDuplicate(w))}
          onArchive={onArchive && (() => onArchive(w))}
          onRestore={onRestore && (() => onRestore(w))}
          onDelete={onDelete && (() => onDelete(w))}
        />
      ),
    },
  ];
};
